import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactSelect, { ActionMeta } from "react-select";
import CreatableReactSelect from "react-select/creatable";
import { ETranslation } from "../../../translations/translation-keys";
import { IOption } from "../Input/Input";

export type TMenuPosition = "fixed" | "absolute";

interface IProps {
  inputName?: string;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  options?: IOption[];
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  isSearchable?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  isCreatable?: boolean;
  isMulti?: boolean;
  placeholder?: string;
  menuPosition?: TMenuPosition;
  hideControls?: boolean;
}

export const getSelectValues = (options: IOption[], values: string[]) => {
  return options.filter((option) => values.includes(option.value.toString()));
};

export const getSelectValue = (options: IOption[], value: string): IOption | undefined => {
  return options.find((option) => option.value.toString() === value.toString());
};

const Select: React.FC<IProps> = ({
  inputName,
  value,
  onChange,
  options,
  disabled,
  invalid,
  className,
  isSearchable,
  isClearable,
  isLoading,
  isMulti,
  isCreatable = false,
  placeholder,
  menuPosition = "absolute",
  hideControls,
}) => {
  const { t } = useTranslation();

  const [selectValue, setSelectValue] = useState<IOption | IOption[] | undefined>();
  const [selectOptions, setSelectOptions] = useState<IOption[]>([]);

  useEffect(() => {
    if (value && options && selectOptions.length > 0) {
      if (isMulti) {
        const values = (value as string[]).map((v) => v.toString());
        setSelectValue(getSelectValues(selectOptions, values));
      } else {
        setSelectValue(getSelectValue(selectOptions, value.toString()));
      }
    } else {
      setSelectValue(undefined);
    }
  }, [value, selectOptions, isMulti, options]);

  useEffect(() => {
    setSelectOptions(
      options
        ? options.map((o) => ({
            label: (o.labelTranslation ? t(o.labelTranslation) : o.label) || o.value,
            value: o.value,
          }))
        : []
    );
  }, [options, t]);

  const changeHandler = (option: any, actionMeta: ActionMeta<any>) => {
    if (isMulti) {
      onChange((option as IOption[] | undefined)?.map((v) => v.value) || []);
    } else {
      onChange((option as IOption | undefined)?.value || "");
    }
  };

  const components = hideControls
    ? { DropdownIndicator: () => null, IndicatorSeparator: () => null, ClearIndicator: () => null }
    : {};

  const customStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    option: (provided: any) => ({ ...provided, color: '#2F2F2F' }),
    singleValue: (provided: any) => ({ ...provided, color: '#2F2F2F' }),
    input: (provided: any) => ({ ...provided, color: '#2F2F2F' }),
    multiValueLabel: (provided: any) => ({ ...provided, color: '#2F2F2F' }),
  };

  return isCreatable ? (
    <CreatableReactSelect
      name={inputName}
      value={selectValue}
      onChange={changeHandler}
      options={selectOptions}
      isDisabled={disabled}
      className={className}
      isSearchable={isSearchable}
      isClearable={isClearable}
      isLoading={isLoading}
      formatCreateLabel={(val) => `${t(ETranslation.REACT_SELECT_ADD_NEW)} "${val}"`}
      placeholder={placeholder || t(ETranslation.REACT_SELECT_PLACEHOLDER)}
      isMulti={isMulti}
      menuPosition={menuPosition}
      menuPortalTarget={menuPosition === "fixed" ? document.body : undefined}
      styles={customStyles}
      components={components}
    />
  ) : (
    <ReactSelect
      name={inputName}
      value={selectValue}
      onChange={changeHandler}
      options={selectOptions}
      isDisabled={disabled}
      className={className}
      isSearchable={isSearchable}
      isClearable={isClearable}
      isLoading={isLoading}
      noOptionsMessage={() => t(ETranslation.NOT_FOUND_ERROR)}
      placeholder={placeholder || t(ETranslation.REACT_SELECT_PLACEHOLDER)}
      isMulti={isMulti}
      menuPosition={menuPosition}
      menuPortalTarget={menuPosition === "fixed" ? document.body : undefined}
      styles={customStyles}
      components={components}
    />
  );
};

export default Select;
